import React, { useRef, useState, useEffect } from 'react';
import { Routes, Route, Outlet, Navigate, useNavigate, useLocation, useOutletContext, Link } from 'react-router-dom';
import "@aws-amplify/ui-react/styles.css";
import { withAuthenticator, Button as AmplifyButton, WithAuthenticatorProps } from '@aws-amplify/ui-react';
import { signOut, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { Button } from 'primereact/button';
import { Menu } from 'primereact/menu';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import Customers from './pages/Customers';
import UserManagement from './pages/UserManagement';
import { Footer } from './components/Footer';

interface AppProps extends WithAuthenticatorProps {}

const App: React.FC<AppProps> = ({ user }) => {
  return (
    <div style={{ 
      height: '100vh',
      width: '100vw',
      display: 'flex', 
      flexDirection: 'column',
      margin: 0,
      padding: 0,
      overflow: 'hidden',
      position: 'fixed',
      top: 0,
      left: 0
    }}>
      <Routes>
        <Route element={<Layout user={user}/>}>
          <Route index element={<Home />} />
          <Route path="/" element={<Home />} />
          <Route path="/user-management" element={<UserManagementGuard />} />
          <Route path="*" element={<p>There's nothing here: 404!</p>} />
        </Route>
      </Routes>
    </div>
  );
};

/**
 * Route guard for /user-management.
 * Redirects to / if the user is not a GS-USER-ADMIN member.
 * Uses groups already fetched by Layout via Outlet context.
 */
const UserManagementGuard: React.FC = () => {
  const { userGroups, groupsLoaded } = useOutletContext<{ userGroups: string[]; groupsLoaded: boolean }>();

  if (!groupsLoaded) return null; // Wait for auth to load
  if (!userGroups.includes('GS-USER-ADMIN')) return <Navigate to="/" replace />;
  return <UserManagement />;
};

interface LayoutProps {
  user?: WithAuthenticatorProps['user'];
}

const Layout: React.FC<LayoutProps> = ({ user }) => {
  const menuRef = useRef<Menu>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [profileDialogVisible, setProfileDialogVisible] = useState(false);
  const [userAttributes, setUserAttributes] = useState<any>({});
  const [userGroups, setUserGroups] = useState<string[]>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);

  useEffect(() => {
    // Fetch user attributes and groups when component mounts
    const loadUserData = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserAttributes(attributes);
        
        const session = await fetchAuthSession();
        const groups = session.tokens?.idToken?.payload["cognito:groups"] as string[] || [];
        setUserGroups(groups);
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setGroupsLoaded(true);
      }
    };
    
    loadUserData();
  }, []);

  async function handleSignOut() {
    try {
        await signOut();
    } catch (error) {
        // Error signing out
    }
  }

  // Get user initials (first letter of username)
  const getUserInitials = () => {
    if (!user?.username) return '?';
    const username = user.username;
    // If email, take first letter before @
    if (username.includes('@')) {
      return username.charAt(0).toUpperCase();
    }
    // If name with space, take first letter of each word
    const parts = username.split(' ');
    if (parts.length > 1) {
      return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    }
    // Otherwise just first letter
    return username.charAt(0).toUpperCase();
  };

  const menuItems = [
    {
      label: user?.username,
      items: [
        {
          label: 'Profile',
          icon: 'pi pi-user',
          command: () => setProfileDialogVisible(true)
        },
        {
          separator: true
        },
        {
          label: 'Sign out',
          icon: 'pi pi-sign-out',
          command: handleSignOut
        }
      ]
    }
  ];

  // Get user attributes in a clean format
  const getUserAttributesDisplay = () => {
    const attributes: { label: string; value: string; icon: string }[] = [];
    
    // Email
    if (userAttributes.email) {
      attributes.push({ 
        label: 'Email', 
        value: userAttributes.email, 
        icon: 'pi-envelope' 
      });
    }
    
    // Organization (custom attribute)
    if (userAttributes['custom:organization']) {
      attributes.push({ 
        label: 'Organization', 
        value: userAttributes['custom:organization'], 
        icon: 'pi-building' 
      });
    }
    
    // Groups/Roles
    if (userGroups.length > 0) {
      attributes.push({ 
        label: 'Role', 
        value: userGroups.join(', '), 
        icon: 'pi-shield' 
      });
    }
    
    // Email verified status
    if (userAttributes.email_verified !== undefined) {
      attributes.push({ 
        label: 'Email Status', 
        value: userAttributes.email_verified === 'true' ? 'Verified ✓' : 'Not Verified', 
        icon: 'pi-check-circle' 
      });
    }
    
    // Phone number (if available)
    if (userAttributes.phone_number) {
      attributes.push({ 
        label: 'Phone', 
        value: userAttributes.phone_number, 
        icon: 'pi-phone' 
      });
    }
    
    // Name (if available)
    if (userAttributes.name) {
      attributes.push({ 
        label: 'Full Name', 
        value: userAttributes.name, 
        icon: 'pi-user' 
      });
    }
    
    return attributes;
  };

  return (
    <>
      <div style={{ 
        backgroundColor: '#f8f9fa',
        padding: '0.75rem 1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #dee2e6',
        minHeight: '3.5rem',
        flexShrink: 0
      }}>
        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
          <h1 className="text-xl font-semibold" style={{ margin: 0, lineHeight: '1', padding: 0, flex: '0 0 auto', cursor: 'pointer' }}>Garage Support Tool</h1>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: '0 0 auto' }}>
          {userGroups.includes('GS-USER-ADMIN') && (
            <Button
              label="User Management"
              icon="pi pi-users"
              className={location.pathname === '/user-management' ? 'p-button-text p-button-plain' : 'p-button-text'}
              style={{ fontWeight: location.pathname === '/user-management' ? 600 : 400 }}
              onClick={() => navigate('/user-management')}
            />
          )}
          <div title={`Logged in as: ${user?.username}`}>
            <Menu model={menuItems} popup ref={menuRef} id="user-menu" />
            <Button 
              className="p-button-rounded p-button-text" 
              style={{ 
                width: '2.5rem', 
                height: '2.5rem', 
                fontSize: '0.875rem',
                fontWeight: '600',
                borderRadius: '50%',
                minWidth: '2.5rem',
                padding: '0',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: 0
              }}
              onClick={(e) => menuRef.current?.toggle(e)}
              aria-controls="user-menu"
              aria-haspopup
            >
              {getUserInitials()}
            </Button>
          </div>
        </div>
      </div>

      {/* Profile Dialog */}
      <Dialog
        header="User Profile"
        visible={profileDialogVisible}
        onHide={() => setProfileDialogVisible(false)}
        className="w-full md:w-30rem"
        dismissableMask
      >
        <div className="flex flex-column align-items-center mb-4">
          {/* Large Avatar */}
          <div 
            className="flex align-items-center justify-content-center"
            style={{
              width: '5rem',
              height: '5rem',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontSize: '2rem',
              fontWeight: '600',
              marginBottom: '1rem',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)'
            }}
          >
            {getUserInitials()}
          </div>
          <h3 className="m-0 mb-1">{user?.username}</h3>
          <span className="text-sm text-500">Active User</span>
        </div>

        <Divider />

        {/* User Attributes */}
        <div className="flex flex-column gap-3">
          {getUserAttributesDisplay().map((attr, index) => (
            <div key={index} className="flex align-items-start gap-3">
              <i 
                className={`pi ${attr.icon} text-primary`} 
                style={{ fontSize: '1.25rem', marginTop: '0.25rem' }}
              ></i>
              <div className="flex-1">
                <div className="text-sm text-500 mb-1">{attr.label}</div>
                <div className="font-semibold">{attr.value}</div>
              </div>
            </div>
          ))}
        </div>

        <Divider />

        {/* Additional Info */}
        <div className="text-sm text-500 text-center">
          <i className="pi pi-info-circle mr-2"></i>
          Contact your administrator to update profile information
        </div>
      </Dialog>

      <main style={{ flex: 1, padding: '1rem', overflow: 'auto' }}>
        <Outlet context={{ userGroups, groupsLoaded }} />
      </main>

      {/* Footer hidden for now - keep code for future use */}
      {/* <Footer /> */}
    </>
  );
};

const Home: React.FC = () => {
  return (
    <>
      <Customers />  
    </>
  )
};

export default withAuthenticator(App, {
  hideSignUp: true
});
